import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Select, Input, Divider, Popconfirm, message } from 'antd';
import { UploadOutlined, DeleteOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getPersonnelFiles, uploadPersonnelFile, deletePersonnelFile, getPersonnelFileTypes, createPersonnelFileType } from '../../api/personnelFiles';

const formatBytes = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PersonnelFilesTab = ({ userId, canManage }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState(null);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [addingType, setAddingType] = useState(false);
  const fileInputRef = useRef();

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await getPersonnelFiles(userId);
      setFiles(res.data.data || []);
    } catch {
      message.error('Dosyalar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await getPersonnelFileTypes();
      setTypes(res.data.data || []);
    } catch {
      message.error('Dosya türleri yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleAddType = async () => {
    const label = newTypeLabel.trim();
    if (!label) return;
    setAddingType(true);
    try {
      const res = await createPersonnelFileType(label);
      setTypes(prev => [...prev, res.data]);
      setTypeId(res.data.id);
      setNewTypeLabel('');
    } catch (err) {
      message.error(err?.response?.data?.message || 'Tür eklenemedi.');
    } finally {
      setAddingType(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!typeId) {
      message.error('Lütfen önce bir dosya türü seçin.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      message.error('Dosya boyutu 30MB sınırını aşıyor.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('personnel_file_type_id', typeId);

    setUploading(true);
    try {
      await uploadPersonnelFile(userId, formData);
      message.success('Dosya yüklendi.');
      fetchFiles();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Dosya yüklenemedi.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePersonnelFile(id);
      message.success('Dosya silindi.');
      setFiles(prev => prev.filter(f => f.id !== id));
    } catch {
      message.error('Dosya silinemedi.');
    }
  };

  const columns = [
    { title: 'Tür', render: (_, r) => r.type?.label || <span className="text-gray-400">-</span> },
    { title: 'Boyut', dataIndex: 'file_size', render: formatBytes },
    {
      title: 'Yükleyen',
      render: (_, r) => r.uploader ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.uploader.id}`)}>
          {r.uploader.name} {r.uploader.surname}
        </Button>
      ) : '-',
    },
    { title: 'Tarih', dataIndex: 'created_at', render: (d) => dayjs(d).format('DD.MM.YYYY HH:mm') },
    {
      title: 'İşlemler',
      render: (_, r) => (
        <div className="flex gap-2">
          <Button size="small" icon={<DownloadOutlined />} href={r.download_url} target="_blank" rel="noreferrer">
            İndir
          </Button>
          {canManage && (
            <Popconfirm title="Dosyayı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(r.id)} okText="Evet" cancelText="Hayır">
              <Button size="small" danger icon={<DeleteOutlined />}>Sil</Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {canManage && (
        <div className="flex items-end gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-sm mb-1">Dosya Türü</div>
            <Select
              placeholder="Tür seçin"
              value={typeId}
              onChange={setTypeId}
              style={{ width: 240 }}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div className="flex gap-2 px-2 pb-2">
                    <Input
                      placeholder="Yeni tür ekle (örn. Sağlık Raporu)"
                      value={newTypeLabel}
                      onChange={(e) => setNewTypeLabel(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      maxLength={150}
                    />
                    <Button icon={<PlusOutlined />} loading={addingType} onClick={handleAddType}>
                      Ekle
                    </Button>
                  </div>
                </>
              )}
            >
              {types.map((t) => (
                <Select.Option key={t.id} value={t.id}>{t.label}</Select.Option>
              ))}
            </Select>
          </div>
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Dosya Seç ve Yükle
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}

      <Table columns={columns} dataSource={files} rowKey="id" loading={loading} pagination={false} />
    </div>
  );
};

export default PersonnelFilesTab;
